import { localized } from '@/i18n/localize'
/** English copy keyed like the mobile ARB keys (auth*) so the i18n layer can replace this file mechanically. */
export const OTP_LENGTH = 4
export const RESEND_SECONDS = 120

export const authStrings = localized({
  back: 'Back',
  securityFooter: '256-bit encrypted · Privacy protected',
  panelTitle: 'Rent, leases and agreements in one place',
  panelSubtitle: 'For landlords and tenants. The same account works in the app and on the web.',
  panelPoints: ['Collect rent online or record cash, with receipts for every payment', 'Create rent agreements and e-sign them without the paperwork', 'Keep tenants, maintenance and documents together'],
  getTheApp: 'Get the mobile app',
  appStore: 'App Store',
  playStore: 'Google Play',
})

export const bootStrings = localized({
  tagline: 'Frictionless property & lease access',
  illustration: 'A building with a lease document and a key',
  loading: 'Loading Leazepro',
  offlineTitle: "You're offline",
  offlineDescription: "Check your connection. We'll try again as soon as you're back online.",
  errorTitle: "We couldn't start Leazepro",
  errorDescription: 'Something went wrong on our side. Please try again in a moment.',
  tryAgain: 'Try again',
})

export const loginStrings = localized({
  title: 'Log in or sign up',
  subtitle: "Enter your mobile number and we'll text you a code to confirm it's you.",
  illustration: 'A security shield over an identity card',
  mobileLabel: 'Mobile number',
  countryHint: 'Code',
  countryLabel: 'Country',
  countrySearchHint: 'Search country or code',
  countryNotFound: 'No country found',
  mobileHint: '0000000000',
  digitCount: (count: number, total: number) => `${count}/${total} digits`,
  invalidMobile: 'Please enter a valid mobile number',
  sendCode: 'Send code',
  sendFailedTitle: "Couldn't send the code",
  sessionEnded: 'Your session ended. Sign in again to continue.',
  termsNotice: ['By continuing, you agree to our ', ' and ', '.'],
  termsLink: 'Terms of Service',
  privacyLink: 'Privacy Policy',
  close: 'Close',
})

export const verifyStrings = localized({
  title: 'Enter the code',
  subtitle: (length: number, mobile: string) => `We sent a ${length}-digit code to ${mobile}.`,
  // Where the code went (LP-1036); SMS keeps `subtitle`. LP-1035 adds the same sentences to the app ARB for Hindi.
  subtitleWhatsApp: (length: number, mobile: string) => `We sent a ${length}-digit code on WhatsApp to ${mobile}.`,
  subtitleEmail: (length: number, email: string) => `We sent a ${length}-digit code to ${email}.`,
  // Email as the backend's own fallback on a first send: no masked address came with it.
  subtitleYourEmail: (length: number) => `We sent a ${length}-digit code to your email.`,
  illustration: 'A phone receiving a one-time code',
  twoFactor: '2-FACTOR AUTH',
  changeNumber: 'Use a different number',
  otpLabel: 'Verification code',
  didntReceive: "Didn't receive the code?",
  resendIn: (time: string) => `Resend code in ${time}`,
  resend: 'Resend code',
  resent: 'New code sent',
  sendOnWhatsApp: 'Send on WhatsApp',
  sendByEmail: 'Send by email',
  sendBySms: 'Send by SMS',
  sentOnWhatsApp: 'Code sent on WhatsApp',
  sentByEmail: 'Code sent by email',
  sentBySms: 'Code sent by SMS',
  resendFailedTitle: "Couldn't send a new code",
  verifyContinue: 'Verify & continue',
  expired: 'That code has expired. Request a new one.',
})

export const profileStrings = localized({
  eyebrow: 'Secure profile setup',
  stepOf: (c: number, t: number) => `Step ${c} of ${t}`,
  aboutTitle: 'Complete your profile',
  aboutSubtitle: 'Add your name, a photo and your email so your leases and rent receipts carry the right details.',
  photoUpload: 'Upload photo',
  photoChange: 'Change photo',
  removePhoto: 'Remove Photo',
  photoFailed: "The photo didn't upload. You can add it later from your account.",
  fileTooLarge: 'File too large. Please choose a smaller image.',
  firstName: 'First Name',
  firstNameHint: 'Asha',
  firstNameInvalid: 'Enter your first name, without spaces',
  lastName: 'Last Name',
  lastNameHint: 'Rao',
  lastNameInvalid: 'Enter your last name, without spaces',
  email: 'Email',
  emailHint: 'asha@example.com',
  emailHelper: 'Leases and rent receipts are sent to this email.',
  emailInvalid: 'Enter a valid email address',
  verifiedPhone: 'Verified phone number',
  verified: 'Verified',
  continue: 'Continue',
  roleTitle: 'How will you use Leazepro?',
  roleSubtitle: 'Pick what fits you best. It decides what you see first.',
  landlordTitle: 'I own properties',
  landlordDescription: 'Manage properties, tenants and rent collection.',
  tenantTitle: 'I rent a home',
  tenantDescription: 'See your rent, pay online and raise requests.',
  roleChangeLater: 'You can change this later in your account settings.',
  saveFailedTitle: "Couldn't save your profile",
  finish: 'Get started',
  back: 'Back',
})

export const emailVerifiedStrings = localized({
  title: 'Email verification',
  verifying: 'Verifying your email…',
  verifiedTitle: 'Email verified',
  verifiedDescription: 'Leases and rent receipts will be sent to this address.',
  expiredTitle: 'This link has expired',
  expiredDescription: 'Verification links work for 7 days. Update your email in Account settings to get a new one.',
  invalidTitle: "This link isn't valid",
  invalidDescription: 'Open the link from the latest email we sent you, or update your email in Account settings.',
  offlineTitle: "You're offline",
  offlineDescription: 'Check your connection and try again.',
  openApp: 'Open LeazePro',
  signIn: 'Continue to sign in',
  tryAgain: 'Try again',
})
